import { useEffect, useState, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { useUserStore } from './store/useUserStore';
import { useAnalysisStore } from './store/useAnalysisStore';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  ANALYSIS_STATUS,
  AI_JOB_PROGRESS_STEPS,
  ON_KIDS_MAIN_KID_ID,
} from './data/define';
import { userApi } from './assets/api/apis';
import { CircularProgress } from '@mui/material';
import Url from './data/url';
import Login from './page/login';
import LoginPass from './page/login/pass';
import LoginWelcome from './page/login/welcome';
import Home from './page/home';
import HomeChangeKids from './page/home/changeKids';
import HomeSelectKid from './page/home/selectKid';
import HomeMultiIntelligenceIntro from './page/home/multiIntelligenceIntro';
import HomeOnkidsAIIntro from './page/home/onkidsAIIntro';
import Report from './page/report';
import ReportDetail from './page/report/detail';
import ReportDetailCommentary from './page/report/commentary';
import History from './page/history';
import HistoryAllVideos from './page/history/allVideos';
import HistoryFavoriteVideos from './page/history/favoriteVideos';
import HistoryWatchVideo from './page/history/watchVideo';
import HistoryIntelligenceVideos from './page/history/intelligenceVideos';
import Education from './page/education';
import Community from './page/community';
import CommunityWritePage from './page/community/write';
import My from './page/my';
import MyParentProfile from './page/my/parentProfile';
import MyKidsManage from './page/my/kidsManage';
import MyKidProfile from './page/my/kidsProfile';
import MyNotice from './page/my/notice';
import MyFrequentQnA from './page/my/qnaFrequent';
import MyPrivateQnA from './page/my/qnaPrivate';
import MyUsageGuide from './page/my/usageGuide';
import RegisterKids from './page/registerkids';
import RegisterKidsUploadVideo from './page/registerkids/uploadVideo';
import RegisterKidsAnalysisResult from './page/registerkids/analysisResult';
import MyList from './page/my/mylist';
import MyCompanyInfo from './page/my/companyInfo';
// import RegisterKidsVideoAnalysis from './page/registerkids/videoAnalysis';

function Router() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [authLoading, setAuthLoading] = useState(true);
  const isLoggedIn = useUserStore((s) => s.isLoggedIn);
  const kids = useUserStore((s) => s.kids);
  const mainKid = useUserStore((s) => s.mainKid);
  const setUser = useUserStore((s) => s.setUser);
  const analysisResult = useAnalysisStore((s) => s.analysisResult);
  const setProgress = useAnalysisStore((s) => s.setLoadingProgress);

  // token check — 최초 1회만 실행 (pathname deps 제거로 매 이동마다 API 호출 방지)
  const hasCheckedRef = useRef(false);
  useEffect(() => {
    if (pathname === Url.LoginPass) {
      setAuthLoading(false);
      return;
    }
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    (async () => {
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (!token) {
        setAuthLoading(false);
        return;
      }
      try {
        const myInfoRes = await userApi.getMyInfo();
        setUser((prev) => ({
          ...prev,
          ...myInfoRes.data,
          isLoggedIn: true,
        }));
      } catch (error) {
        const status = (error as AxiosError)?.response?.status;
        if (status === 401 || status === 403) {
          // 인증/권한 에러 — 확실히 무효한 토큰이므로 제거 + 로그아웃
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          setUser((prev) => (prev.isLoggedIn ? { ...prev, isLoggedIn: false } : prev));
          navigate(Url.Login, { replace: true });
        } else {
          // 5xx / 네트워크 등 일시 장애 — 토큰이 있으니 세션을 낙관적으로 유지한다.
          // 토큰이 실제로 무효라면 이후 인증 API의 401을 axios interceptor가 정리한다.
          setUser((prev) => (prev.isLoggedIn ? prev : { ...prev, isLoggedIn: true }));
        }
      } finally {
        setAuthLoading(false);
      }
    })();
  }, [setUser, pathname, navigate]);

  // mainKid setting — 선택한 아이가 없거나 목록에 없을 때만 보정한다(user-172)
  useEffect(() => {
    if (!isLoggedIn || kids.length === 0) return;
    const current = mainKid ? kids.find((kid) => kid.id === mainKid.id) : undefined;
    if (current) {
      try {
        localStorage.setItem(ON_KIDS_MAIN_KID_ID, String(current.id));
      } catch {
        // 저장소 접근 불가 — 선택 유지만 생략
      }
      return;
    }
    let savedId: string | null = null;
    try {
      savedId = localStorage.getItem(ON_KIDS_MAIN_KID_ID);
    } catch {
      savedId = null;
    }
    const nextKid = kids.find((kid) => String(kid.id) === savedId) ?? kids[0];
    setUser((prev) => ({
      ...prev,
      mainKid: nextKid,
    }));
  }, [isLoggedIn, kids, mainKid, setUser]);

  // video analysis progress update globally
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (analysisResult.status === ANALYSIS_STATUS.PENDING) {
      // 서버 단계값 사이를 1씩 채우되 다음 단계값 직전에서 멈춘다. 100→0 되돌림 없음(user-176)
      timer = setInterval(() => {
        setProgress((prev) => {
          const nextStep = AI_JOB_PROGRESS_STEPS.find((step) => step > prev) ?? 100;
          return prev + 1 < nextStep ? prev + 1 : prev;
        });
      }, 3000);
    } else if (useAnalysisStore.getState().loadingProgress !== 0) {
      // 이미 0이면 setState 불필요 — 스토어 변경 방지
      setProgress(0);
    }
    return () => clearInterval(timer);
  }, [analysisResult, setProgress]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  return (
    <Routes>
      {!isLoggedIn && (
        <>
          <Route path={Url.Root} element={<Login />} />
          <Route path={Url.Home} element={<Home />} />
          <Route path={Url.Login} element={<Login />} />
          <Route path={Url.LoginPass} element={<LoginPass />} />
          <Route path={Url.LoginWelcome} element={<LoginWelcome />} />
          <Route path={Url.Community} element={<Community />} />
          <Route path={Url.CommunityWrite} element={<CommunityWritePage />} />
          <Route path={Url.HomeMultiIntelligenceIntro} element={<HomeMultiIntelligenceIntro />} />
          <Route path={Url.HomeOnkidsAIIntro} element={<HomeOnkidsAIIntro />} />
          <Route path="*" element={<Navigate to={Url.Root} replace />} />
          <Route path={Url.CompanyInfo} element={<MyCompanyInfo />} />
        </>
      )}
      {isLoggedIn && (
        <>
          <Route path={Url.Root} element={<Home />} />
          <Route path={Url.LoginPass} element={<LoginPass />} />
          <Route path={Url.LoginWelcome} element={<LoginWelcome />} />
          <Route path={Url.HomeChangeKids} element={<HomeChangeKids />} />
          <Route path={Url.HomeSelectKid} element={<HomeSelectKid />} />
          <Route path={Url.HomeMultiIntelligenceIntro} element={<HomeMultiIntelligenceIntro />} />
          <Route path={Url.HomeOnkidsAIIntro} element={<HomeOnkidsAIIntro />} />
          <Route path={Url.Report} element={<Report />} />
          <Route path={Url.ReportDetail} element={<ReportDetail />} />
          <Route path={Url.ReportDetailCommentary} element={<ReportDetailCommentary />} />
          <Route path={Url.History} element={<History />} />
          <Route path={Url.HistoryAllVideos} element={<HistoryAllVideos />} />
          <Route path={Url.HistoryFavoriteVideos} element={<HistoryFavoriteVideos />} />
          <Route path={Url.HistoryWatchVideo} element={<HistoryWatchVideo />} />
          <Route path={Url.HistoryIntelligenceVideos} element={<HistoryIntelligenceVideos />} />
          <Route path={Url.Education} element={<Education />} />
          <Route path={Url.Community} element={<Community />} />
          <Route path={Url.CommunityWrite} element={<CommunityWritePage />} />
          <Route path={Url.My} element={<My />} />
          <Route path={Url.MyList} element={<MyList />} />
          <Route path={Url.MyParentProfile} element={<MyParentProfile />} />
          <Route path={Url.MyKidsManage} element={<MyKidsManage />} />
          <Route path={Url.MyKidProfile} element={<MyKidProfile />} />
          <Route path={Url.MyNotice} element={<MyNotice />} />
          <Route path={Url.MyFrequentQnA} element={<MyFrequentQnA />} />
          <Route path={Url.MyPrivateQnA} element={<MyPrivateQnA />} />
          <Route path={Url.MyUsageGuide} element={<MyUsageGuide />} />
          <Route path={Url.MyCompanyInfo} element={<MyCompanyInfo />} />
          <Route path={Url.KidsRegister} element={<RegisterKids />} />
          <Route path={Url.KidsRegisterUpload} element={<RegisterKidsUploadVideo />} />
          {/* <Route path={Url.KidsVideoAnalysis} element={<RegisterKidsVideoAnalysis />} /> */}
          <Route path={Url.KidsAnalysisResult} element={<RegisterKidsAnalysisResult />} />
          {/* Catch-all route for any undefined paths */}
          <Route path="*" element={<Navigate to={Url.Root} replace />} />
        </>
      )}
    </Routes>
  );
}

export default Router;
